import { event } from "@foretagsplatsen/yaem";

/**
 * A `Wizard` contains a sequence of steps (instances of
 * `AbstractWizardStep`). It starts with its first step and finishes
 * when its last step sends its `submitSuccess` event.
 *
 * The class `Wizard` is itself a subclass of `AbstractWizardStep`
 * making it theoretically possible to embed a wizard in another
 * wizard.
 *
 * A wizard passes a context (any object) along each of its steps, one
 * at a time. This way, each step gets a chance to read and write
 * shared values while it is active.
 *
 * When the current step is completed the event `currentStepCompleted`
 * is triggered. At this time, the step can be submitted with
 * `submitCurrentStep()` and the `currentStepSubmitted` is
 * triggered. If the submission succeeds, the wizard moves on to the next
 * step automatically. If the submission fails because of exceptions, the
 * wizard stays at the current step. Exceptions can be either errors
 * or warnings. If there is at least one error, the current step must
 * be submitted again to move forward. If all exceptions are warnings,
 * the client can send `ignoreWarnings()` to move forward.
 *
 * The event `currentStepChanged` is triggered when (1) a step starts,
 * (2) the current step fails to submit, or (3) the current step
 * triggers a `changed` event.
 *
 * A document containing a wizard only needs to use the `addSteps()`
 * and `start()` methods. The other public methods are used for
 * rendering.
 *
 * @param {function} [onSuccess] - Callback invoked with the wizard state
 *   once the wizard is successfully completed
 */
export default class Wizard {
	constructor({ onSuccess = () => {} } = {}) {
		this._state = {};
		this._steps = [];
		this._currentStepIndex = undefined;
		this._isStarted = false;

		this.currentStepCompleted = event();
		this.currentStepChanged = event();
		this.currentStepSubmitted = event();
		this.aborted = event();
		this._onSuccess = onSuccess;
	}

	/**
	 * Return a widget rendering the current step.
	 */
	stepRenderable() {
		return this.currentStep().getRenderable();
	}

	/**
	 * Add the parameter as the last step of the receiver.
	 */
	addStep(step) {
		this._steps.push(step);
	}

	/**
	 * Add the parameter as the last steps of the receiver.
	 */
	addSteps(steps) {
		steps.forEach((step) => this.addStep(step));
	}

	/**
	 * Start the first step of the wizard.
	 */
	start({ context = {} } = {}) {
		this._state = context;
		this._isStarted = true;
		this._startStep(0);
	}

	/**
	 * Return true iff the wizard has been started.
	 */
	isStarted() {
		return this._isStarted;
	}

	/**
	 * Return true iff the current step is completed (i.e., it is ok
	 * to submit it) and there is no submission in progress.
	 *
	 * Note: should answer true even if at last step as this one
	 * should be submitted too.
	 */
	canSubmitCurrentStep() {
		const currentStep = this.currentStep();

		return currentStep && !!currentStep.canBeSubmitted();
	}

	/**
	 * Return true if the current step is allowed to navigate
	 * to the previous step.
	 */
	canGoToPreviousStep() {
		return (
			!this.isAtFirstStep() && this.currentStep()?.canGoToPreviousStep()
		);
	}

	currentStepIsSubmitting() {
		const currentStep = this.currentStep();

		return currentStep && currentStep.isSubmitting();
	}

	currentStepSubmitExceptions() {
		return this.currentStep().getSubmitExceptions();
	}

	/**
	 * Submit the current step so that we can move on to the next when
	 * the step sends the `submitSuccess` event. Only use this method
	 * if `canSubmitCurrentStep()` returns `true`.
	 */
	submitCurrentStep() {
		this.currentStep().submit();
	}

	isAtFirstStep() {
		return this._currentStepIndex === 0;
	}

	isAtLastStep() {
		return this._currentStepIndex === this.getLastStepIndex();
	}

	getCurrentStepIndex() {
		return this._currentStepIndex;
	}

	getLastStepIndex() {
		return this._steps.length - 1;
	}

	currentStep() {
		return this._steps[this._currentStepIndex];
	}

	hasSteps() {
		return this._steps.length > 0;
	}

	getSteps() {
		return this._steps;
	}

	getStepTitles() {
		return this._steps.map((step) => step.getTitle());
	}

	/**
	 * Starts the step before the current one. Prevent the former from
	 * auto-submitting on start.
	 */
	goToPreviousStep() {
		this._startStep(this._currentStepIndex - 1, true);
	}

	/**
	 * Go to the next step, ignoring the warnings sent by the current
	 * one. This should only be called after the current step has been
	 * submitted and returned no errors but only warnings.
	 */
	ignoreWarnings() {
		this._goToNextStep();
	}

	/**
	 * Return true iff the final step is ready to be submitted.
	 */
	_isCompleted() {
		const currentStep = this.currentStep();

		return (
			currentStep && this.isAtLastStep() && !!currentStep.canBeSubmitted()
		);
	}

	_doSubmit() {
		// no-op because the Wizard automatically finishes when the
		// last step sends `submitSuccess`.
	}

	_goToNextStep() {
		if (this.isAtLastStep()) {
			this._unregisterFromCurrentStepEvents();
			this._onSuccess();
			return;
		}

		this._startStep(this._currentStepIndex + 1);
	}

	_startStep(stepIndex, preventSubmitOnStart = false) {
		this._unregisterFromCurrentStepEvents();
		this._currentStepIndex = stepIndex;
		this._registerToCurrentStepEvents();

		this.currentStep().start({
			context: this._state,
			preventSubmitOnStart,
		});

		this.currentStepChanged.trigger();
	}

	_registerToCurrentStepEvents() {
		const step = this.currentStep();

		this._currentStepCompletedBinding = step.completed.register(() => {
			this.currentStepCompleted.trigger();
		});

		this._currentStepSubmittedBinding = step.submitted.register(() => {
			this.currentStepSubmitted.trigger();
		});

		this._currentStepSubmitSuccessBinding = step.submitSuccess.register(
			() => {
				this._goToNextStep();
			},
		);

		this._currentStepSubmitExceptionBinding = step.submitException.register(
			() => {
				this.currentStepChanged.trigger();
			},
		);

		this._currentStepChangedBinding = step.changed.register(() => {
			this.currentStepChanged.trigger();
		});
	}

	_unregisterFromCurrentStepEvents() {
		const step = this.currentStep();

		if (!step) {
			// this happens if we have just started the wizard
			return;
		}

		step.completed.unregister(this._currentStepCompletedBinding);
		step.submitted.unregister(this._currentStepSubmittedBinding);
		step.submitSuccess.unregister(this._currentStepSubmitSuccessBinding);

		step.submitException.unregister(
			this._currentStepSubmitExceptionBinding,
		);

		step.changed.unregister(this._currentStepChangedBinding);
	}
}
